// Basic logic of the code comes from smarter people: https://codesandbox.io/p/sandbox/to6uf?file=%2Fsrc%2FApp.tsx%3A60%2C3-76%2C4
import {animated, to as interpolate, useSprings} from '@react-spring/web'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {HiMiniHandThumbDown, HiMiniHeart} from 'react-icons/hi2'
import {useDrag} from 'react-use-gesture'
import {useTranslations} from 'use-intl'
import {useIsClient} from 'usehooks-ts'
import clsx from 'clsx'
import {DesignCollectionItem} from '~/client/spreadshop/missing-types'

// These two are just helpers, they curate spring data, values that are later being interpolated into css
const to = (i: number) => ({
    x: 0,
    y: i * -4,
    scale: 1,
    rot: -10 + Math.random() * 20,
    delay: i * 100,
})
const from = (_i: number) => ({x: 0, rot: 0, scale: 1.5, y: -1000})
// This is being used down there in the view, it interpolates rotation and scale into a css transform
const trans = (r: number, s: number) =>
    `perspective(1500px) rotateX(5deg) rotateY(${r / 10}deg) rotateZ(${r}deg) scale(${s})`

type PerfectFitProps = {
    designs: DesignCollectionItem[]
    onFinish: (liked: DesignCollectionItem[], disliked: DesignCollectionItem[]) => void
}

const Stack = ({designs, onFinish}: PerfectFitProps) => {
    const t = useTranslations('perfectFit.stack')
    const isClient = useIsClient()
    const likedDesignsRef = useRef(new Set<number>())
    const dislikedDesignsRef = useRef(new Set<number>())
    const activeDesignRef = useRef<number | undefined>(designs.length - 1)
    const [isInitialized, setIsInitialized] = useState<boolean>(false)

    const [props, api] = useSprings(designs.length, (i) => ({
        ...to(i),
        from: from(i),
        onRest: () => setIsInitialized(true),
    })) // Create a bunch of springs using the helpers above

    useEffect(() => {
        if (!isInitialized) {
            return
        }

        activeDesignRef.current = designs.length - 1
        likedDesignsRef.current.clear()
        dislikedDesignsRef.current.clear()
        setIsInitialized(false)

        api.start((i) => ({
            ...to(i),
            from: from(i),
            onStart: () => setIsInitialized(true),
        }))
    }, [designs])

    const startAnimation = useCallback(
        (index: number, dir: number, mx: number, down: boolean, velocity: number) => {
            const liked = likedDesignsRef.current
            const disliked = dislikedDesignsRef.current

            api.start((i) => {
                if (index !== i) return
                const isGone = disliked.has(index) || liked.has(index)
                const x = isGone ? (200 + window.innerWidth) * dir : down ? mx : 0
                const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0)
                const scale = down ? 1.1 : 1
                return {
                    x,
                    rot,
                    scale,
                    delay: undefined,
                    config: {friction: 50, tension: down ? 800 : isGone ? 200 : 500},
                }
            })

            if (!down && disliked.size + liked.size === designs.length)
                setTimeout(() => {
                    const likedDesigns = [...liked].map((index) => designs[index])
                    const dislikedDesigns = [...disliked].map((index) => designs[index])

                    disliked.clear()
                    liked.clear()
                    onFinish(likedDesigns, dislikedDesigns)
                }, 300)
        },
        [api, designs, onFinish]
    )

    const likeDesign = useCallback(() => {
        const designIndex = activeDesignRef.current ?? 0
        likedDesignsRef.current.add(designIndex)
        activeDesignRef.current = designIndex === 0 ? undefined : designIndex - 1
        startAnimation(designIndex, 1, 1, false, 0.2)
    }, [startAnimation])

    const dislikeDesign = useCallback(() => {
        const designIndex = activeDesignRef.current ?? 0
        dislikedDesignsRef.current.add(designIndex)
        activeDesignRef.current = designIndex === 0 ? undefined : designIndex - 1
        startAnimation(designIndex, -1, -1, false, 0.2)
    }, [startAnimation])

    const bind = useDrag(
        ({args: [index], down, movement: [mx], velocity}) => {
            const liked = likedDesignsRef.current
            const disliked = dislikedDesignsRef.current

            const trigger = Math.abs(mx) > 50
            const dir = mx < 0 ? -1 : 1

            if (!down && trigger) {
                dir < 0 ? disliked.add(index) : liked.add(index)
                activeDesignRef.current = index === 0 ? undefined : index - 1
            }

            startAnimation(index, dir, mx, down, velocity)
        },
        {axis: 'x'}
    )

    return (
        <div className="relative size-full">
            {props.map(({x, y, rot, scale}, i) => (
                <animated.div
                    className="absolute flex w-full aspect-square touch-none items-center justify-center will-change-transform scale-75"
                    key={i}
                    style={{x, y}}
                >
                    {/* This is the card itself, we're binding our gesture to it (and inject its index so we know which is which) */}
                    <animated.div
                        {...bind(i)}
                        className={clsx(
                            'size-full rounded-lg bg-[#16161d]/95 drop-shadow-lg will-change-transform transform-gpu select-none p-4',
                            'cursor-grab active:cursor-grabbing active:backdrop-blur-lg'
                        )}
                        style={{
                            transform: interpolate([rot, scale], trans),
                        }}
                    >
                        <img
                            className="size-full object-center object-contain select-none touch-none"
                            src={designs[i].image.src ?? ''}
                            srcSet={designs[i].image.srcset ?? undefined}
                            sizes="(min-width: 640px) 1000px, 500px"
                            alt={designs[i].image.altText ?? undefined}
                            loading="lazy"
                            draggable={false}
                        />
                    </animated.div>
                </animated.div>
            ))}
            {isClient && (
                <>
                    <button
                        onClick={() => dislikeDesign()}
                        className={clsx(
                            'absolute -top-6 left-4 md:-left-6 rounded-full p-4 bg-red-500/80 backdrop-blur transform-gpu drop-shadow transition-all',
                            isInitialized
                                ? 'opacity-100 translate-y-0 translate-x-0 animate-flying delay-1000 hover:paused'
                                : 'opacity-0 -translate-y-3 -translate-x-3'
                        )}
                    >
                        <HiMiniHandThumbDown className="size-6" />
                        <span className="sr-only">{t('dislike')}</span>
                    </button>
                    <button
                        onClick={() => likeDesign()}
                        className={clsx(
                            'absolute -bottom-6 right-4 md:-right-6  rounded-full p-3.5 bg-green-500/80 backdrop-blur transform-gpu drop-shadow transition-all',
                            isInitialized
                                ? 'opacity-100 translate-y-0 translate-x-0 animate-flying delay-500 hover:paused'
                                : 'opacity-0 translate-y-3 translate-x-3'
                        )}
                    >
                        <HiMiniHeart className="size-7" />
                        <span className="sr-only">{t('like')}</span>
                    </button>
                </>
            )}
        </div>
    )
}

export default Stack
